import { IVariation } from "@/interfaces/sku.interface";

export const parseVariationsFromString = (variationsString: string): IVariation[] => {
  if (!variationsString || variationsString.trim() === "") {
    return [];
  }

  try {
    const parsed = JSON.parse(variationsString);
    return Array.isArray(parsed) ? parsed : [parsed];
  } catch {
    return [];
  }
};

export const stringifyVariations = (variations: IVariation[]): string => {
  if (!variations || variations.length === 0) {
    return "";
  }
  return JSON.stringify(variations);
};

export const hasVariations = (variationsString: string | IVariation[]): boolean => {
  if (typeof variationsString === "string") {
    return variationsString.trim() !== "";
  }
  return Array.isArray(variationsString) && variationsString.length > 0;
};

export const getVariationCodeFromVariation = (variation: any): number => {
  return variation.VariationCode || variation.code;
};

export const getVariationNameFromVariation = (variation: any): string => {
  return variation.VariationName || variation.name;
};

export const normalizeVariationToBackend = (variation: any) => ({
  VariationCode: getVariationCodeFromVariation(variation),
  VariationName: getVariationNameFromVariation(variation),
  VariationValue: variation.VariationValue || "",
});

export const normalizeVariationsToBackend = (variations: any[]) => {
  return variations.map(normalizeVariationToBackend);
};

export const normalizeVariationToFrontend = (variation: any) => ({
  code: String(getVariationCodeFromVariation(variation)),
  name: getVariationNameFromVariation(variation),
});

export const normalizeVariationsToFrontend = (variations: any[]): any[] => {
  if (!variations) return [];
  return variations.map(normalizeVariationToFrontend);
};

export const getDefaultVariationName = (variationCode: number): string => {
  return `VARIACAO_${variationCode}`;
};

export const createEmptyVariation = (code: number, name: string): IVariation => ({
  VariationCode: code,
  VariationName: name,
  VariationValue: "",
});

export const mergeProductAndSkuVariations = (
  productVariations: any[],
  skuVariations: IVariation[]
): IVariation[] => {
  const mergedVariations: IVariation[] = [];

  productVariations.forEach((prodVar: any) => {
    const variationCode = Number(prodVar.code ?? prodVar.VariationCode);
    const existingVar = skuVariations.find((skuVar) => skuVar.VariationCode === variationCode);

    if (existingVar) {
      mergedVariations.push(existingVar);
    } else {
      mergedVariations.push(
        createEmptyVariation(variationCode, prodVar.name ?? prodVar.VariationName ?? "")
      );
    }
  });

  return mergedVariations;
};

export const formatVariationsLabel = (
  skuVariations?: string | IVariation[],
  productVariations?: any[]
): string => {
  if (!skuVariations) return "-";

  try {
    const variationsArray =
      typeof skuVariations === "string"
        ? skuVariations.trim() === ""
          ? []
          : parseVariationsFromString(skuVariations)
        : skuVariations;

    if (!Array.isArray(variationsArray) || variationsArray.length === 0) {
      return "-";
    }

    const ordered =
      productVariations && productVariations.length > 0
        ? mergeProductAndSkuVariations(productVariations, variationsArray)
        : variationsArray;

    return ordered.map((v) => `${v.VariationName}: ${v.VariationValue || "-"}`).join(", ");
  } catch {
    return "-";
  }
};

export const filterRemovedVariations = (
  variations: IVariation[],
  removedVariationCodes: string[]
): IVariation[] => {
  return variations.filter(
    (v) => !removedVariationCodes.includes(String(getVariationCodeFromVariation(v)))
  );
};
